import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, MessageCircle, DollarSign, Users,
  TrendingUp, BarChart3, Calendar, ArrowUpRight, ExternalLink, BookOpen,
  Home as HomeIcon,
} from 'lucide-react';
import AdminShell from '../components/AdminShell';
import TodayScheduleWidget from '../components/TodayScheduleWidget';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFunds: 0, contributions: 0, volunteers: 0,
    whatsappGroups: 0, campaigns: 0, youtuberSignups: 0,
  });

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { navigate('/admin/login'); return; }
      try {
        await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } });
        const [s, g, c, yt] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/whatsapp-groups`),
          axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/youtuber-program/stats`).catch(() => ({ data: { total_signups: 0 } })),
        ]);
        setStats({
          totalFunds: s.data.total_funds,
          contributions: s.data.contribution_count,
          volunteers: s.data.volunteer_count,
          whatsappGroups: g.data.length,
          campaigns: c.data.length,
          youtuberSignups: yt.data.total_signups,
        });
      } catch {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const adminEmail = localStorage.getItem('admin_email') || '';
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const statCards = [
    { label: 'Total Raised', value: `₹${stats.totalFunds.toLocaleString('en-IN')}`, delta: '+ this year', icon: DollarSign, tint: '#fff0f5', fg: '#e5266a' },
    { label: 'Contributions', value: stats.contributions, delta: 'all-time', icon: TrendingUp, tint: '#eef2ff', fg: '#4f46e5' },
    { label: 'Volunteers', value: stats.volunteers, delta: 'registered', icon: Users, tint: '#ecfdf5', fg: '#059669' },
    { label: 'WhatsApp Groups', value: stats.whatsappGroups, delta: 'active cities', icon: MessageCircle, tint: '#f0fdf4', fg: '#16a34a' },
    { label: 'YouTuber Signups', value: stats.youtuberSignups, delta: 'of 100 target', icon: BarChart3, tint: '#faf5ff', fg: '#7c3aed' },
    { label: 'Campaigns', value: stats.campaigns, delta: 'created', icon: Calendar, tint: '#fff7ed', fg: '#ea580c' },
  ];

  const tools = [
    { to: '/admin/appointments', icon: Calendar, tint: '#e0f2fe', fg: '#0284c7', title: 'Appointment Slots', desc: 'Open or close hourly slots for DigiMitra & YouTuber calls' },
    { to: '/admin/bookings', icon: BookOpen, tint: '#fef3c7', fg: '#b45309', title: 'All Bookings', desc: 'Full list of DigiMitra visits + YouTuber signups with date & time' },
    { to: '/admin/content', icon: FileText, tint: '#fce7f3', fg: '#db2777', title: 'Content Management', desc: 'Edit text, titles, and SEO across all pages' },
    { to: '/admin/whatsapp-groups', icon: MessageCircle, tint: '#dcfce7', fg: '#16a34a', title: 'WhatsApp Groups', desc: 'Manage city-based WhatsApp community groups' },
    { to: '/admin/campaigns', icon: DollarSign, tint: '#dbeafe', fg: '#2563eb', title: 'Fundraising Campaigns', desc: 'Create and manage fundraising campaigns' },
  ];

  const quickLinks = [
    { href: '/', icon: HomeIcon, label: 'Website' },
    { href: '/digimitra', icon: Users, label: 'DigiMitra' },
    { href: '/youtuber-program', icon: BarChart3, label: 'YouTuber' },
    { href: '/donate', icon: DollarSign, label: 'Donate' },
  ];

  return (
    <AdminShell>
      <SEO title="Admin Dashboard - Elderra" description="Elderra admin management dashboard" />

      {/* Greeting header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{today}</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }} data-testid="admin-dash-greeting">
            {greeting}<span style={{ color: '#e5266a' }}>.</span>
          </h1>
          <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>Here's what's happening across Elderra today.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', padding: '8px 14px', borderRadius: 999, border: '1px solid #e5e7eb' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #e5266a, #7c3aed)', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
            {(adminEmail[0] || 'A').toUpperCase()}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{adminEmail}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading dashboard...</div>
      ) : (
        <>
          {/* Stats */}
          <section data-testid="admin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 36 }}>
            {statCards.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}
                  style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid #e5e7eb', transition: 'transform 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: s.tint, color: s.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={20} />
                    </div>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{s.label}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{s.delta}</span>
                  </div>
                </div>
              );
            })}
          </section>

          {/* Today's Schedule Widget */}
          <TodayScheduleWidget />

          {/* Tools */}
          <section data-testid="admin-tools" style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Management Tools</h2>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{tools.length} modules</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {tools.map((t) => {
                const Icon = t.icon;
                return (
                  <a
                    key={t.to}
                    href={t.to}
                    onClick={(e) => { e.preventDefault(); navigate(t.to); }}
                    data-testid={`admin-tool-${t.title.toLowerCase().replace(/\s/g, '-')}`}
                    style={{ display: 'flex', gap: 14, padding: 18, background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', textDecoration: 'none', color: 'inherit', alignItems: 'center', transition: 'all 0.15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.fg; e.currentTarget.style.transform = 'translateX(2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: t.tint, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{t.title}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>{t.desc}</div>
                    </div>
                    <ArrowUpRight size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />
                  </a>
                );
              })}
            </div>
          </section>

          {/* Quick Links */}
          <section data-testid="admin-quick-links">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>Public Site</h2>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Open in new tab</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {quickLinks.map((l) => {
                const Icon = l.icon;
                return (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none', transition: 'all 0.15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                  >
                    <Icon size={14} /> {l.label} <ExternalLink size={12} style={{ color: '#9ca3af' }} />
                  </a>
                );
              })}
            </div>
          </section>
        </>
      )}
    </AdminShell>
  );
};

export default AdminDashboard;
